import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import { Toaster } from 'sonner';
import HashScrollHandler from './components/HashScrollHandler';
import SEOHead from './components/seo/SEOHead';

const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const PracticeAreas = lazy(() => import('./pages/PracticeAreas'));
const Resources = lazy(() => import('./pages/Resources'));
const Contact = lazy(() => import('./pages/Contact'));
const ClientPortalLogin = lazy(() => import('./pages/ClientPortalLogin'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const BlogEditor = lazy(() => import('./pages/BlogEditor'));
const NewsletterEditor = lazy(() => import('./pages/NewsletterEditor'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const NotFound = lazy(() => import('./pages/NotFound'));

const pageSeo = {
  '/': {
    title: 'PFA & Associates LLP | Chartered Accountants',
    description: 'PFA & Associates LLP delivers audit, tax, and advisory services for businesses in India with practical financial guidance and compliance support.',
    path: '/',
    type: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'AccountingService',
      name: 'PFA & Associates LLP',
      url: 'https://pfaandassociates.com/',
      logo: 'https://pfaandassociates.com/images/company-logo.png',
      serviceType: ['Audit Services', 'Tax Advisory', 'Accounting Services', 'Business Consulting']
    }
  },
  '/about': {
    title: 'About PFA & Associates LLP',
    description: 'Learn about PFA & Associates LLP, a chartered accountancy firm focused on trusted audit, tax planning, and business advisory services.',
    path: '/about',
    type: 'website'
  },
  '/services': {
    title: 'CA Services for Growing Businesses',
    description: 'Explore professional CA services including audit, accounting, taxation, compliance, and strategic advisory for sustainable business growth.',
    path: '/services',
    type: 'website'
  },
  '/practice-areas': {
    title: 'Industries We Serve | PFA LLP',
    description: 'Discover industry-specific accounting and advisory expertise for manufacturing, healthcare, retail, technology, and other sectors.',
    path: '/practice-areas',
    type: 'website'
  },
  '/resources': {
    title: 'Blogs and Newsletters | PFA Resources',
    description: 'Read expert blogs, tax updates, and business newsletters from PFA & Associates LLP to stay informed on finance and compliance.',
    path: '/resources',
    type: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'PFA Resources',
      url: 'https://pfaandassociates.com/resources'
    }
  },
  '/contact': {
    title: 'Contact PFA & Associates LLP',
    description: 'Contact PFA & Associates LLP for audit, tax, and advisory assistance. Share your requirements and our team will connect quickly.',
    path: '/contact',
    type: 'website'
  },
  '/client-portal': {
    title: 'Client Portal Login | PFA LLP',
    description: 'Secure client portal login via ERPCA.',
    path: '/client-portal',
    type: 'website',
    robots: 'noindex, nofollow'
  },
  '/admin/login': {
    title: 'Admin Login | PFA LLP',
    description: 'Secure admin login for PFA & Associates LLP website management.',
    path: '/admin/login',
    robots: 'noindex, nofollow'
  },
  '/admin': {
    title: 'Admin Panel | PFA LLP',
    description: 'Internal administration panel for managing PFA & Associates LLP website content.',
    path: '/admin',
    robots: 'noindex, nofollow'
  }
};

const RouteShell = ({ PageComponent, withChrome = true, seoOverride = {} }) => {
  const location = useLocation();
  const matchedSeo = pageSeo[location.pathname] || {
    title: 'PFA & Associates LLP',
    description: 'PFA & Associates LLP - Chartered Accountants',
    path: location.pathname
  };
  const seo = { ...matchedSeo, ...seoOverride };

  return (
    <>
      <SEOHead {...seo} />
      {withChrome ? <Header /> : null}
      <main aria-label="Main content">
        <PageComponent />
      </main>
      {withChrome ? <Footer /> : null}
    </>
  );
};

const BlogDetailShell = () => {
  const location = useLocation();
  return (
    <RouteShell
      PageComponent={BlogDetail}
      seoOverride={{
        title: 'Blog Details | PFA Resources',
        description: 'Read detailed insights from PFA & Associates LLP on accounting, taxation, compliance, and business strategy.',
        path: location.pathname,
        type: 'article'
      }}
    />
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <HashScrollHandler />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-600">Loading page...</div>}>
          <Routes>
            {/* Redirect variants */}
            <Route path="/home" element={<Navigate to="/" replace />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<RouteShell PageComponent={AdminLogin} withChrome={false} />} />
            <Route path="/admin" element={<RouteShell PageComponent={AdminPanel} />} />
            <Route path="/admin/blog/new" element={<RouteShell PageComponent={BlogEditor} seoOverride={{ title: 'Create Blog | Admin', robots: 'noindex, nofollow', path: '/admin/blog/new' }} />} />
            <Route path="/admin/blog/edit/:id" element={<RouteShell PageComponent={BlogEditor} seoOverride={{ title: 'Edit Blog | Admin', robots: 'noindex, nofollow' }} />} />
            <Route path="/admin/newsletter/new" element={<RouteShell PageComponent={NewsletterEditor} seoOverride={{ title: 'Create Newsletter | Admin', robots: 'noindex, nofollow', path: '/admin/newsletter/new' }} />} />
            <Route path="/admin/newsletter/edit/:id" element={<RouteShell PageComponent={NewsletterEditor} seoOverride={{ title: 'Edit Newsletter | Admin', robots: 'noindex, nofollow' }} />} />

            {/* Public Routes */}
            <Route path="/" element={<RouteShell PageComponent={Home} />} />
            <Route path="/about" element={<RouteShell PageComponent={About} />} />
            <Route path="/services" element={<RouteShell PageComponent={Services} />} />
            <Route path="/practice-areas" element={<RouteShell PageComponent={PracticeAreas} />} />
            <Route path="/resources" element={<RouteShell PageComponent={Resources} />} />
            <Route path="/blog/:id" element={<BlogDetailShell />} />
            <Route path="/contact" element={<RouteShell PageComponent={Contact} />} />
            <Route path="/client-portal" element={<RouteShell PageComponent={ClientPortalLogin} />} />

            {/* 404 */}
            <Route
              path="*"
              element={
                <>
                  <SEOHead
                    title="Page Not Found | PFA LLP"
                    description="The page you requested could not be found. Explore PFA & Associates LLP services and resources."
                    path="/404"
                    robots="noindex, follow"
                  />
                  <Header />
                  <NotFound />
                  <Footer />
                </>
              }
            />
          </Routes>
        </Suspense>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
